import {escapeRegex} from 'common/regex.js';
import http from 'http.js';
import ko from 'knockout';
import user from 'accounts/models/user.js';

class NameResult {
    constructor(data, manager) {
        this.id = ko.observable(data.id);
        this.title = ko.observable(data.title);
        this.firstName = ko.observable(data.first_name);
        this.lastName = ko.observable(data.last_name);
        this.avatar = ko.observable(data.avatar);
        this.url = ko.observable(data.profile_url);
        this.locality = ko.observable(data.location);
        this.specialties = ko.observableArray();

        this.displayName = ko.pureComputed(() => {
            const name = `${
                this.title() || ''
            } ${this.firstName()} ${this.lastName()}`;
            return name.replace(
                new RegExp(`(${escapeRegex(manager.nameQuery())})`, 'gi'),
                '<span class="highlight">$1</span>',
            );
        });

        for (const value of data.specialties) {
            if (typeof manager.specialtiesMap[value] !== 'undefined') {
                this.specialties.push(manager.specialtiesMap[value]);
            }
        }
    }
}

export default class NameSearchManager {
    constructor(linkAttrs, elem) {
        this.linkAttrs = linkAttrs;
        this.loading = ko.observable(false);
        this.nameQuery = ko.observable('').extend({
            'rateLimit': 200,
            'method': 'notifyWhenChangesStop',
        });
        this.results = ko.observableArray();
        this.page = ko.observable(1);
        this.totalCount = ko.observable(-1);
        this.selectedIndex = ko.observable(-1);

        this.specialtiesMap = {};
        this.limit = 10;

        // flag to show/hide 'no result' message after a name result has been selected
        this.hasSearchValue = ko.observable(false);

        this.nameQuery.subscribe(() => {
            this.search(true);
            this.hasSearchValue(true);
        });

        this.hasMore = ko.pureComputed(
            () =>
                this.totalCount() >= 0 &&
                this.page() * this.limit < this.totalCount(),
        );

        // Handle scroll state of the search results container
        this.selectedIndex.subscribe((val) => {
            if (elem) {
                const results = elem.getElementsByClassName('results')[0];
                if (val >= 0) {
                    const result = results.getElementsByTagName('li')[val];
                    const resultOffset =
                        result.offsetTop + result.offsetHeight;
                    const resultsOffset =
                        results.offsetHeight + results.scrollTop;
                    if (resultOffset > resultsOffset) {
                        // handle scrolling down
                        results.scrollTop += resultOffset - resultsOffset;
                    } else if (result.offsetTop < results.scrollTop) {
                        // handle scrolling up
                        results.scrollTop = result.offsetTop;
                    }
                } else {
                    results.scrollTop = 0;
                }
            }
        });

        this.select = this.select.bind(this);
    }

    async loadSpecialties({showProFilter = null} = {}) {
        if (showProFilter) {
            this.show = showProFilter;
        } else {
            const u = user;
            u.load().then(() => {
                // limit to referrals if user is a health professional
                this.show = u.userType() === 'H' ? 'referrals' : 'all';
            });
        }
        http.get({
            url: '/api/professionals/v1/specialties/',
            data: {show: this.show},
        }).then((data) => {
            for (const value of data) {
                this.specialtiesMap[value.id] = value.professional_name;
            }
        });
    }

    showMore() {
        if (this.hasMore()) {
            this.page(this.page() + 1);
            this.search(false);
        }
    }

    clear() {
        this.hasSearchValue(false);
        this.results([]);
        this.totalCount(-1);
        this.selectedIndex(-1);
        this.page(1);
        this.loading(false);
    }

    select(result) {
        let url = result.url();
        if (this.linkAttrs) {
            url += '?';
            for (const key of Object.keys(this.linkAttrs)) {
                url += key + '=' + this.linkAttrs[key];
            }
        }
        window.open(url, '_top');
    }

    search(clearResults) {
        if (this._abortController) {
            this._abortController.abort();
            this._abortController = null;
        }
        if (this.nameQuery().length < 3) {
            this.clear();
            return;
        }
        if (clearResults) {
            this.clear();
        }
        this.loading(true);

        this._abortController = new AbortController();

        const args = {
            name: this.nameQuery(),
            show: this.show,
            limit: this.limit,
        };
        if (this.page() > 1) {
            args.page = this.page();
        }
        http.get({
            url: '/api/professionals/v1/search/',
            data: args,
            signal: this._abortController.signal,
        })
            .then((data) => {
                this.totalCount(data.count);
                this.loading(false);
                for (const value of data.results) {
                    this.results.push(new NameResult(value, this));
                }
            })
            .catch((error) => {
                // Ignore aborted requests (user typed more characters)
                if (
                    error.code === 'ERR_CANCELED' ||
                    error.name === 'CanceledError'
                ) {
                    return;
                }
                this.loading(false);
                console.error('Search failed:', error);
            });
    }

    keydown(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (!this.results().length) {
            return true;
        }
        if (key === 38 || key === 'Up' || key === 'ArrowUp') {
            this.selectedIndex(Math.max(0, this.selectedIndex() - 1));
            return;
        } else if (key === 40 || key === 'Down' || key === 'ArrowDown') {
            const idx = Math.min(
                this.results().length - 1,
                this.selectedIndex() + 1,
            );
            this.selectedIndex(idx);
            return;
        } else if (key === 27 || key === 'Escape') {
            this.clear();
            return;
        }
        return true;
    }

    keypress(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (key === 13 || key === 'Enter') {
            // ENTER
            // If results box is open and a result is highlighted intercept
            if (this.results().length && this.selectedIndex() > -1) {
                this.select(this.results()[this.selectedIndex()]);
                this.clear();
                return;
            }
        }
        return true; // Continue with event
    }
}
